import React, { useContext, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { HubProgressBar } from 'dorian-shared/types/branch/HubOption';
import { HUB_MAX_PROGRESS_BARS } from '../hubStepDefaults';
import {
  MemoryDTO,
  MemoryType,
} from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { NodeEditContext } from '../../../context/NodeEditContext';
import { ProgressBarRow } from './ProgressBarRow';
import './ProgressBars.scss';

const MOUSE_SENSOR_OPTIONS = { activationConstraint: { distance: 5 } };
const KEYBOARD_SENSOR_OPTIONS = { coordinateGetter: sortableKeyboardCoordinates };
const DND_MODIFIERS = [restrictToVerticalAxis];

interface ProgressBarsListProps {
  bars: HubProgressBar[];
  // Live/published: backend only persists label + sortOrder. Add/remove/variable-ref
  // changes must be blocked — label input and DnD reorder stay enabled.
  isStructuralLocked: boolean;
  isAtLimit: boolean;
  onAdd: () => void;
  onUpdate: (uuid: string, changes: Partial<HubProgressBar>) => void;
  onRemove: (uuid: string) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

export function ProgressBarsList({
  bars,
  isStructuralLocked,
  isAtLimit,
  onAdd,
  onUpdate,
  onRemove,
  onDragEnd,
}: ProgressBarsListProps) {
  const ctx = useContext(NodeEditContext) as Record<string, unknown> | undefined;
  const ctxMemoryBankSlots = ctx?.memoryBankSlots as MemoryDTO[] | undefined;

  const numericVariables = useMemo(
    () => (ctxMemoryBankSlots ?? []).filter((slot: MemoryDTO) => slot.type === MemoryType.Number),
    [ctxMemoryBankSlots],
  );

  const sortableItems = useMemo(() => bars.map((bar) => bar.uuid), [bars]);

  const mouseSensor = useSensor(MouseSensor, MOUSE_SENSOR_OPTIONS);
  const touchSensor = useSensor(TouchSensor);
  const keyboardSensor = useSensor(KeyboardSensor, KEYBOARD_SENSOR_OPTIONS);
  const sensors = useSensors(mouseSensor, touchSensor, keyboardSensor);

  const isAddDisabled = isStructuralLocked || isAtLimit;

  return (
    <div className="progress-bars-list" data-testid="progress-bars-list">
      <p className="progress-bars-list__helper text-muted small mb-1">
        Each bar shows label + numerator/denominator on the client. Max {HUB_MAX_PROGRESS_BARS} bars.
      </p>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={DND_MODIFIERS}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
          {bars.map((bar) => (
            <ProgressBarRow
              key={bar.uuid}
              bar={bar}
              variables={numericVariables}
              isStructuralLocked={isStructuralLocked}
              onChange={onUpdate}
              onRemove={onRemove}
            />
          ))}
        </SortableContext>
      </DndContext>

      <div className="d-flex align-items-center mt-2">
        <Button
          variant="secondary"
          size="sm"
          disabled={isAddDisabled}
          onClick={onAdd}
        >
          + Add Progress Bar
        </Button>
        <small className="text-muted ml-2">
          {bars.length} / {HUB_MAX_PROGRESS_BARS}
        </small>
      </div>
    </div>
  );
}
